import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    school_name: '', school_slug: '', school_email: '', school_phone: '', school_city: '',
    admin_first_name: '', admin_last_name: '', admin_email: '', admin_password: '',
  });

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleSchoolName = (e) => {
    const name = e.target.value;
    setForm({ ...form, school_name: name, school_slug: generateSlug(name) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await publicApi.registerSchool(form);
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#fafaf8] flex items-center justify-center px-6">
        <Card className="max-w-md w-full border-stone-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-stone-900 mb-2">Inscription reussie !</h2>
            <p className="text-stone-500 text-sm leading-relaxed mb-6">
              Votre ecole <strong>{form.school_name}</strong> a ete enregistree avec succes.
              Un administrateur va valider votre inscription sous peu. Vous recevrez une notification des que votre espace sera active.
            </p>
            <Button onClick={() => navigate('/')} className="bg-emerald-700 hover:bg-emerald-800" data-testid="back-home-btn">
              Retour a l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      {/* Nav */}
      <nav className="bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-emerald-700 flex items-center justify-center">
                <BookOpen className="w-3 h-3 text-white" />
              </div>
              <span className="font-semibold text-stone-900">Daaraj</span>
            </div>
          </button>
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-stone-900">Inscrire votre ecole</h1>
          <p className="text-stone-500 text-sm mt-1">Etape {step} sur 2</p>
          <div className="flex gap-2 justify-center mt-4">
            <div className={`h-1.5 w-16 rounded-full ${step >= 1 ? 'bg-emerald-600' : 'bg-stone-200'}`} />
            <div className={`h-1.5 w-16 rounded-full ${step >= 2 ? 'bg-emerald-600' : 'bg-stone-200'}`} />
          </div>
        </div>

        <Card className="border-stone-200">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit}>
              {step === 1 && (
                <div className="space-y-4">
                  <CardHeader className="p-0 pb-4">
                    <CardTitle className="text-base">Informations de l'ecole</CardTitle>
                  </CardHeader>
                  <div>
                    <Label>Nom de l'ecole *</Label>
                    <Input value={form.school_name} onChange={handleSchoolName} placeholder="Ex: Ecole Al-Azhar" required data-testid="school-name-input" />
                  </div>
                  <div>
                    <Label>Identifiant (slug) *</Label>
                    <Input value={form.school_slug} onChange={set('school_slug')} placeholder="al-azhar" required data-testid="school-slug-input" />
                    <p className="text-xs text-stone-400 mt-1">Sera utilise dans l'URL: {form.school_slug || 'votre-ecole'}.daaraj.sn</p>
                  </div>
                  <div>
                    <Label>Email de l'ecole *</Label>
                    <Input type="email" value={form.school_email} onChange={set('school_email')} placeholder="contact@ecole.sn" required data-testid="school-email-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Telephone</Label>
                      <Input value={form.school_phone} onChange={set('school_phone')} placeholder="+221 77 123 45 67" data-testid="school-phone-input" />
                    </div>
                    <div>
                      <Label>Ville</Label>
                      <Input value={form.school_city} onChange={set('school_city')} placeholder="Dakar" data-testid="school-city-input" />
                    </div>
                  </div>
                  <Button type="button" onClick={() => setStep(2)} className="w-full bg-emerald-700 hover:bg-emerald-800 mt-2" data-testid="next-step-btn">
                    Suivant
                  </Button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <CardHeader className="p-0 pb-4">
                    <CardTitle className="text-base">Compte administrateur</CardTitle>
                  </CardHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Prenom *</Label>
                      <Input value={form.admin_first_name} onChange={set('admin_first_name')} required data-testid="admin-firstname-input" />
                    </div>
                    <div>
                      <Label>Nom *</Label>
                      <Input value={form.admin_last_name} onChange={set('admin_last_name')} required data-testid="admin-lastname-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Email administrateur *</Label>
                    <Input type="email" value={form.admin_email} onChange={set('admin_email')} required data-testid="admin-email-input" />
                  </div>
                  <div>
                    <Label>Mot de passe *</Label>
                    <Input type="password" value={form.admin_password} onChange={set('admin_password')} required minLength={6} data-testid="admin-password-input" />
                  </div>
                  <div className="flex gap-3 mt-2">
                    <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1" data-testid="prev-step-btn">
                      Retour
                    </Button>
                    <Button type="submit" disabled={loading} className="flex-1 bg-emerald-700 hover:bg-emerald-800" data-testid="submit-register-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Inscrire l\'ecole'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
